<template>
    <modal @close="close" dialog="modal-wide" customClass="heightAuto"
        class="reservation-modal editReservationModal payment dark-top pb-3" :footer="false">
        <div slot="title" ref="modalTopAnchor" class="pt-3" v-if="reservation">
            <title-area :reservation="reservation"
                @titleAreaIsReadyToSubmit="($event) => titleAreaIsReadyToSubmit = $event" />
        </div>

        <div slot="body" class="d-flex flex-column bg-lighter position-relative">
            <prebook-overlay v-if="showPrebookOverlay"></prebook-overlay>
            <div v-else>
                <!-- Popups -->
                <create-invoice-modal :reservation="reservation" v-if="showCreateInvoiceModal"
                    :transactionTotal="reservation.billingTotal"
                    @close="toggleCreateInvoiceModal"></create-invoice-modal>
                <edit-extras :reservation="reservation" :showAddPayment="showAddPayment"
                    v-if="showEditExtras"></edit-extras>
                <view-extras :reservation="reservation" :showAddPayment="showAddPayment"
                    v-if="showViewExtras"></view-extras>
                <add-extras :reservation="reservation" v-if="showAddExtras"></add-extras>
                <edit-transport :reservation="reservation" :showAddPayment="showAddPayment"
                    v-if="showEditTransport"></edit-transport>
                <invoice-modal v-if="showInvoiceModal" :reservation="reservation"
                    @toggleInvoiceModal="toggleInvoiceModal"></invoice-modal>

                <!-- Top Message -->
                <div v-if="!isSavingChanges" class="unsavedChanges bg-color-1 lightest text-center"
                    :class="{ 'active': reservationHasUnsavedChanges }">
                    Don't forget to
                    <span class="bold" @click="handleSaveChanges"
                        style="text-decoration: underline; cursor: pointer;">Save Changes</span>.
                </div>
                <div v-else class="unsavedChanges bg-color-1 lightest text-center"
                    :class="{ 'active': reservationHasUnsavedChanges }">
                    Saving Changes.
                </div>

                <!-- Main Area -->
                <div class="d-flex">
                    <!-- Loading message -->
                    <div class="col-sm-10 text-center dark fs-larger p-5"
                        v-if="!reservation && !getIsLoadingReservations">
                        Please wait while we load your reservation.
                    </div>

                    <!-- Not Loading -->
                    <div class="col-sm-10" v-else>
                        <!-- Top Area Info -->
                        <top-area :reservation="reservation"
                            @topAreaIsReadyToSubmit="($event) => topAreaIsReadyToSubmit = $event"
                            class="mt-2"></top-area>

                        <!-- Tabs -->
                        <tabs class="reservation-edit__tabs" :options="{ useUrlFragment: true }"
                            @changed="onTabChanged">
                            <!-- Reservation Details -->
                            <tab name="Reservation">
                                <tab-reservation :reservation="reservation"
                                    :tabClass="tabWarnings.reservation ? 'warning' : null"
                                    @toggleEditPaxReservation="toggleEditPaxReservation"></tab-reservation>
                            </tab>

                            <!-- Customer Details -->
                            <tab name="Customer" :tabClass="tabWarnings.customer ? 'warning' : null">
                                <tab-customer :disabled="getIsSavingReservation" :reservation="reservation"
                                    @phone-valid="isPhoneValid = !!$event"></tab-customer>
                            </tab>

                            <!-- Billing -->
                            <tab name="Billing">
                                <tab-billing v-if="reservation" :reservation="reservation"
                                    @toggleInvoiceModal="toggleInvoiceModal" />
                            </tab>

                            <!-- Payments -->
                            <tab name="Payments" :tabClass="tabWarnings.payments ? 'warning' : null">
                                <tab-payments :reservation="reservation" :disabled="getIsSavingReservation" />
                            </tab>

                            <!-- Logs -->
                            <tab name="Logs" :tabClass="tabWarnings.logs ? 'warning' : null">
                                <tab-logs :reservation="reservation" :disabled="getIsSavingReservation" />
                            </tab>
                        </tabs>
                    </div>

                    <!-- Left Panel -->
                    <div class="col-sm-2 pl-0">
                        <!-- Side Buttons -->
                        <side-buttons :disabled="getIsSavingReservation || isEditingTabReservation"
                            :topAreaIsReadyToSubmit="topAreaIsReadyToSubmit"
                            :titleAreaIsReadyToSubmit="titleAreaIsReadyToSubmit" :reservation="reservation"
                            :isPhoneValid="isPhoneValid" :showAddPayment="showAddPayment"
                            v-on:toggleAddPayment="toggleAddPayment" @updateReservationsList="reservationUpdated"
                            @updateThisReservation="updateThisReservation" :isToSaveChanges="isToSaveChanges"
                            @changesSaved="changesSaved" @savingChanges="($event) => isSavingChanges = $event">
                        </side-buttons>
                    </div>
                </div>

                <div class="pl-3 pr-3 pb-3" v-if="showExtraLogsSection">
                    <tab-logs :reservation="reservation" :disabled="getIsSavingReservation" :isResume="true" />

                    <div class="d-flex justify-content-start">
                        <a href="#" class="reservations__anchor-see-more-logs my-2 fs-small"
                            @click.prevent="goToLogs">See All Logs</a>
                    </div>
                </div>
            </div>
        </div>
        <div slot="footer" class="hidden"></div>
    </modal>
</template>

<script>
import { reservationsMixin } from '@/mixins/reservations'
import { mapGetters, mapActions } from 'vuex'

import Modal from '@/tools/modal/Modal'

// Parts
import TitleArea from './ReservationEditV2/TitleArea'
import TopArea from './ReservationEditV2/TopArea'
import PrebookOverlay from './ReservationEditV2/PrebookOverlay.vue'

// Tabs
import TabReservation from './ReservationEditV2/TabReservation/TabReservation'
import TabCustomer from './ReservationEditV2/TabCustomer/TabCustomer'
import TabBilling from './ReservationEditV2/TabBilling/TabBilling'
import TabPayments from './ReservationEditV2/TabPayments/TabPayments'
import TabLogs from './ReservationEditV2/TabLogs/TabLogs'

// Popups
import EditExtras from './ReservationEditV2/EditExtras'
import ViewExtras from './ReservationEditV2/ViewExtras'
import AddExtras from './ReservationEditV2/AddExtras'
import EditTransport from './ReservationEditV2/EditTransport'
import InvoiceModal from '@/tools/Invoice//InvoiceModal'
import CreateInvoiceModal from '@/tools/Invoice/CreateInvoice'

// SideButtons
import SideButtons from './ReservationEditV2/SideButtons'

export default {
    data() {
        return {
            isPhoneValid: true,
            isEditingTabReservation: false,
            isLogsSelected: false,
            reservation: null,
            showAddPayment: false,
            showEditExtras: false,
            showViewExtras: false,
            showAddExtras: false,
            showEditTransport: false,
            showInvoiceModal: false,
            showCreateInvoiceModal: false,
            isToSaveChanges: false,
            isSavingChanges: false,
            topAreaIsReadyToSubmit: false,
            titleAreaIsReadyToSubmit: false,
        }
    },
    watch: {
        'getSelectedReservation': {
            handler(newVal, oldVal) {
                this.reservation = newVal;
                this.initializeBookingComponents();
            },
        }
    },
    created() {
        this.initializeBookingComponents()
        // Make sure to reset getReservationCancelNow to its default value
        this.setReservationCancelNow(true);

        // Make sure to return false in getReservationCreation to avoid conflict with payments
        this.unsetReservationCreation();

        // Make sure to reset getReservationCancelNow to its default value
        this.setReservationCancelNow(true);

        // If for some reason the reservation has no resource we set it to the first available on the event
        this.reservation = this.getSelectedReservation;
        this.setReservationBackup(JSON.parse(JSON.stringify(this.reservation)));

        // Something went worng
        if (!this.reservation.billing) {
            this.showSomethingWentWrongPopup(true);
        }

        this.setReservationCalculations(this.reservation);

        if (!this.reservation.updateFlags) {
            this.$set(this.reservation, 'updateFlags', {});
        }

        // Check if the event has mandatory extras and show them as a popup
        this.showMandatoryExtras(this.getSelectedReservation);

        // Cleans calculationsGUID as this is a new reservation
        this.resetCalculationsGuid();

        // Save the original status, if the status is changed only triggers warnings when pressing "save"
        this.saveOriginalStatus();

        // Update this reservatio -> receives an updated version of the current reservation and puts it on the modal
        this.eventHub.$on('updateThisReservation', updatedReservation => {
            this.updateThisReservation(updatedReservation);
        })

        // Listener to toggle edit extras
        this.eventHub.$on('editReservation_toggleEditExtas', data => {
            this.showEditExtras = !this.showEditExtras;

            if (data) {
                this.reservation.bookingComponents = data;
            }

            this.setReservationCalculations(this.reservation);
        })

        // Listener to toggle edit extras
        this.eventHub.$on('editReservation_toggleViewExtas', data => {
            this.showViewExtras = !this.showViewExtras;

            if (data && data !== 'addExtraTransport') {
                this.reservation.bookingComponents = data;
            } else if (data === 'addExtraTransport') {
                this.showEditTransport = !this.showEditTransport;
            }

            this.removeEmptyComponents();
            this.setReservationCalculations(this.reservation);
        })

        // Listener to open create invoice modal
        this.eventHub.$on('editReservation_toggleCreateInvoice', () => {
            this.toggleCreateInvoiceModal();
        })

        // Listener to toggle add extras
        this.eventHub.$on('editReservation_toggleAddExtras', selectedSeller => {
            if (selectedSeller != 'close') {
                // Increment "addExtras" to existing extras and requests calculations
                if (this.showAddExtras) {
                    this.reservation.event.eventComponents.map(eventExtra => {
                        var extra = JSON.parse(JSON.stringify(eventExtra));
                        extra.id = null;

                        if (extra.quantityToAdd > 0) {
                            extra.quantity = extra.quantityToAdd;
                            extra.channel = selectedSeller;

                            if (selectedSeller.channelType) {
                                extra.channelType = selectedSeller.channelType;
                                extra.creatorUserAccount = this.account;
                                extra.creatorSupplier = null;
                                extra.creatorAgent = null;
                            } else if (selectedSeller.userAccountTypeDefault || typeof selectedSeller.accessType == "number") {
                                extra.creatorUserAccount = selectedSeller;
                                extra.creatorSupplier = selectedSeller.supplier;
                                extra.creatorAgent = null;
                                extra.channelType = selectedSeller.salesChannel.channelType;
                            } else {
                                extra.creatorUserAccount = this.account;
                                extra.creatorSupplier = null;
                                extra.creatorAgent = selectedSeller;
                                extra.channel = selectedSeller.salesChannel;
                                extra.channelType = selectedSeller.salesChannel.channelType;
                            }

                            extra.commissionComponentValue = selectedSeller.commissionData ? selectedSeller.commissionData.commissionComponentValue : 0;
                            extra.commissionComponentValueType = selectedSeller.commissionData ? selectedSeller.commissionData.commissionComponentValueType : 0;

                            let addNewComponent = true;
                            this.reservation.bookingComponents.map(bookingComponent => {
                                if (extra.id == null && bookingComponent.id == null && bookingComponent.componentPrice.id == extra.componentPrice.id && bookingComponent.component.id == extra.component.id &&
                                    (
                                        (bookingComponent.creatorUserAccount && bookingComponent.creatorUserAccount.id ? bookingComponent.creatorUserAccount.id : null) ===
                                        (extra.creatorUserAccount && extra.creatorUserAccount.id ? extra.creatorUserAccount.id : null)
                                    ) &&
                                    (
                                        (bookingComponent.creatorAgent && bookingComponent.creatorAgent.id ? bookingComponent.creatorAgent.id : null) ===
                                        (extra.creatorAgent && extra.creatorAgent.id ? extra.creatorAgent.id : null)
                                    ) &&
                                    bookingComponent.channelType === extra.channelType &&
                                    bookingComponent.channel.id === extra.channel.id

                                ) {
                                    addNewComponent = false;

                                    if (bookingComponent.quantity < bookingComponent.initialValue) {
                                        bookingComponent.quantity++;
                                    } else {
                                        bookingComponent.quantity += extra.quantityToAdd;

                                        if (bookingComponent.hasOwnProperty('quantityToAdd') && bookingComponent.quantityToAdd > 0) {
                                            bookingComponent.quantityToAdd += extra.quantityToAdd;
                                        } else {
                                            this.$set(bookingComponent, 'quantityToAdd', extra.quantityToAdd);
                                        }
                                    }

                                    if (bookingComponent.originalQuantity !== 0 || extra.quantity !== 0) {
                                        bookingComponent.quantityAvailable = extra.quantityAvailable;
                                    }

                                    bookingComponent.creatorUserAccount = extra.creatorUserAccount;
                                    bookingComponent.creatorSupplier = extra.creatorSupplier;
                                    bookingComponent.creatorAgent = extra.creatorAgent;
                                }

                                if (bookingComponent.componentPrice.id === extra.componentPrice.id && bookingComponent.component.id === extra.component.id) {
                                    this.$set(bookingComponent, 'quantityAvailable', extra.quantityAvailable);
                                }
                            });

                            const existingBookingComponent = this.reservation.bookingComponents.find(bookingComponent => {
                                return (
                                    bookingComponent.component &&
                                    bookingComponent.componentPrice &&
                                    bookingComponent.component.id === extra.component.id &&
                                    bookingComponent.componentPrice.id === extra.componentPrice.id &&
                                    bookingComponent.channelType === extra.channelType &&
                                    bookingComponent.channel.id === extra.channel.id &&
                                    (
                                        (bookingComponent.creatorUserAccount && bookingComponent.creatorUserAccount.id ? bookingComponent.creatorUserAccount.id : null) ===
                                        (extra.creatorUserAccount && extra.creatorUserAccount.id ? extra.creatorUserAccount.id : null)
                                    ) &&
                                    (
                                        (bookingComponent.creatorAgent && bookingComponent.creatorAgent.id ? bookingComponent.creatorAgent.id : null) ===
                                        (extra.creatorAgent && extra.creatorAgent.id ? extra.creatorAgent.id : null)
                                    )
                                );
                            });

                            if (existingBookingComponent) {
                                existingBookingComponent.quantity += extra.quantity;
                                existingBookingComponent.quantityToAdd = (existingBookingComponent.quantityToAdd || 0) + (extra.quantityToAdd || 0);
                                existingBookingComponent.quantityAvailable = extra.quantityAvailable;
                                existingBookingComponent.creatorUserAccount = extra.creatorUserAccount;
                                existingBookingComponent.creatorSupplier = extra.creatorSupplier;
                                existingBookingComponent.creatorAgent = extra.creatorAgent;

                                addNewComponent = false;
                            }

                            if (addNewComponent) {
                                this.reservation.bookingComponents.push(extra);
                            }
                        }
                    });

                    this.removeEmptyComponents();
                    this.setReservationCalculations(this.reservation);
                }
            } else {
                // Re-adds selected to Availability
                this.reservation.event.eventComponents.map(eventExtra => {
                    eventExtra.quantityAvailable += eventExtra.quantityToAdd;
                });
            }

            this.showAddExtras = !this.showAddExtras;
        })

        // Listener to toggle edit transport
        this.eventHub.$on('editReservation_toggleEditTransport', () => {
            // Increment "addExtras" to existing extras and requests calculations
            if (this.showEditTransport) {
                const { creatorUserAccount, creatorSupplier, creatorAgent, channelType } = this.reservation

                this.reservation.event.eventComponents.map(eventExtra => {
                    let extra = JSON.parse(JSON.stringify(eventExtra))
                    extra.id = null
                    if (extra.quantityToAdd > 0) {
                        extra.quantity = extra.quantityToAdd

                        extra.creatorUserAccount = creatorUserAccount;
                        extra.creatorSupplier = creatorSupplier;
                        extra.creatorAgent = creatorAgent;
                        extra.channelType = channelType;
                        extra.commissionComponentValue = 0;
                        extra.commissionComponentValueType = 0;

                        let addNewComponent = true
                        this.reservation.bookingComponents.map(bookingComponent => {
                            if (bookingComponent.id == null && bookingComponent.componentPrice.id == extra.componentPrice.id) {
                                addNewComponent = false;
                                bookingComponent.quantity = extra.quantityToAdd;
                                bookingComponent.creatorUserAccount = extra.creatorUserAccount;
                                bookingComponent.creatorSupplier = extra.creatorSupplier;
                                bookingComponent.creatorAgent = extra.creatorAgent;
                            }
                        })

                        if (addNewComponent) {
                            this.reservation.bookingComponents.push(extra);
                        }
                    } else {
                        // When removing an added (unsaved) extra
                        this.reservation.bookingComponents.map(bookingComponent => {
                            if (bookingComponent.id == null && bookingComponent.componentPrice.id == extra.componentPrice.id) {
                                bookingComponent.quantity = 0;
                            }
                        });
                    }
                })

                this.setReservationCalculations(this.reservation);
            }

            this.showEditTransport = !this.showEditTransport;
        });

        // Update refund from this reservation
        this.eventHub.$on('updateRefund', (refund) => {
            this.updateExistingRefund(refund);
        });
    },
    beforeDestroy() {
        this.eventHub.$off('editReservation_toggleEditExtas');
        this.eventHub.$off('editReservation_toggleViewExtas');
        this.eventHub.$off('editReservation_toggleAddExtras');
        this.eventHub.$off('editReservation_toggleCreateInvoice');
        this.eventHub.$off('updateCalendar');
        this.eventHub.$off('updateThisReservation');
        this.resetLastSavedReservation();
        this.resetReservationBackup();
        this.resetTabHash();
        window.removeEventListener('hashchange', this._onHash);
    },
    computed: {
        ...mapGetters([
            'account',
            'getSelectedReservation',
            'getCompanySettings',
            'getShowInvoiceOption',
            'getIsSavingReservation',
            'getSalesChannels',
            'getShowInvoiceOption',
        ]),
        showExtraLogsSection() {
            const audits = this.reservation && this.reservation.audits;

            return !this.isLogsSelected && Array.isArray(audits) && audits.length > 0;
        },
        showPrebookOverlay() {
            return this.reservation && (this.reservation.status == 50 || this.reservation.status == -50);
        },
        tabWarnings() {
            return {
                reservation: false,
                customer: this.customerWarning,
                invoices: this.invoiceWarning,
                payments: false,
                logs: false
            }
        },
        customerWarning() {
            return this.reservation.note && this.reservation.note.length > 0;
        },
        invoiceWarning() {
            return this.reservation.billing.details.postalCode && this.reservation.billing.details.postalCode.length > 0
                || this.reservation.billing.details.city && this.reservation.billing.details.city.length > 0
                || this.reservation.billing.details.vat && this.reservation.billing.details.vat.length > 0
                || this.reservation.billing.details.address && this.reservation.billing.details.address.length > 0
                || this.reservation.billing.details.businessName && this.reservation.billing.details.businessName.length > 0
        }
    },
    mixins: [
        reservationsMixin
    ],
    methods: {
        ...mapActions([
            'closeModal',
            'setReservationCalculations',
            'resetCalculationsGuid',
            'showSomethingWentWrongPopup',
            'setSelectedReservation',
            'openReservationById',
            'setReservationCancelNow',
            'unsetReservationCreation',
            'resetLastSavedReservation',
            'setReservationBackup',
            'resetReservationBackup'
        ]),
        onTabChanged(tab) {
            // vue-tabs-component passa o tab; o nome está em tab.tab.name (v2) ou tab.props.name (algumas versões)
            const name =
                (tab && tab.tab && tab.tab.name) ||
                (tab && tab.props && tab.props.name) ||
                (tab && tab.name) || '';

            this.isLogsSelected = name === 'Logs';
            // não precisas mexer no hash aqui; a lib já trata disso
        },
        isLogsSelectedFromHash() {
            return window.location.hash.toLowerCase() === '#logs';
        },
        goToLogs() {
            const h = '#logs';

            if ((window.location.hash || '').toLowerCase() === h) {
                window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }

            window.location.hash = h;
        },
        resetTabHash() {
            if (window.location.hash === '#logs') {
                window.history.replaceState(null, '', '#reservation');
                this.activeTab = 'Reservation';
            }
        },
        removeEmptyComponents() {
            if (!Array.isArray(this.reservation.bookingComponents)) return;

            this.reservation.bookingComponents = this.reservation.bookingComponents.filter(
                component => component.quantityToAdd !== 0
            );
        },
        initializeBookingComponents() {
            // Initializes directly
            this.getSelectedReservation.bookingComponents.forEach(bookComp => {
                this.$set(bookComp, 'initialValue', bookComp.quantity);
            });

            // Synchronizes the value in the LastSavedReservation
            if (this.getLastSavedReservation && this.getLastSavedReservation.bookingComponents) {
                this.getLastSavedReservation.bookingComponents.forEach((savedComp, index) => {
                    this.$set(savedComp, 'initialValue', savedComp.quantity);
                });
            }
        },
        changesSaved() {
            this.isToSaveChanges = false;
            this.eventHub.$emit('changesSaved');
        },
        handleSaveChanges() {
            if (this.reservation.customer.phone && !this.isPhoneValid) return;
            this.isToSaveChanges = true;
        },
        toggleEditPaxReservation(val) {
            this.isEditingTabReservation = val;
        },
        updateExistingRefund(updatedRefund) {
            let oldRefundsArray = JSON.parse(JSON.stringify(this.reservation.refunds));
            // Locate refund to update
            let foundRefund = oldRefundsArray.find(refund => refund.id == updatedRefund.id);
            let foundRefundIndex = oldRefundsArray.indexOf(foundRefund);

            oldRefundsArray[foundRefundIndex] = updatedRefund;

            this.$set(this.reservation, 'refunds', oldRefundsArray);
        },
        close() {
            this.closeModal();
        },
        toggleAddPayment() {
            this.showAddPayment = !this.showAddPayment;
        },
        saveOriginalStatus() {
            this.$set(this.reservation, 'originalStatus', this.reservation.status);
        },
        reservationUpdated() {
            this.reservation.event.eventComponents.forEach(eventComponent => {
                this.$set(eventComponent, 'quantityToAdd', 0);
            });

            this.resetUpdateFlags();
        },
        updateThisReservation(updatedReservation) {
            if (!updatedReservation) {
                return;
            }
            const clonedReservation = JSON.parse(JSON.stringify(this.reservation));
            this.reservation = updatedReservation;

            this.setSelectedReservation({ payload: updatedReservation, isToResetFlags: true }).then(() => {
                this.$set(this.reservation, 'updateFlags', {});
                this.setReservationCalculations(this.reservation);
            })

            if (clonedReservation) this.setReservationBackup(clonedReservation);
        },
        resetUpdateFlags() {
            this.$set(this.reservation, 'updateFlags', {});
        },
        toggleInvoiceModal() {
            this.showInvoiceModal = !this.showInvoiceModal;
        },
        toggleCreateInvoiceModal(toggleTo) {
            if (!toggleTo) {
                this.showCreateInvoiceModal = !this.showCreateInvoiceModal;
            } else {
                this.showCreateInvoiceModal = toggleTo;
            }
        },
    },
    components: {
        Modal,
        TitleArea,
        SideButtons,
        TopArea,
        TabReservation,
        TabCustomer,
        TabPayments,
        TabBilling,
        TabLogs,
        EditExtras,
        ViewExtras,
        AddExtras,
        EditTransport,
        InvoiceModal,
        CreateInvoiceModal,
        PrebookOverlay,
    }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/_variables.scss';

.unsavedChanges {
    max-height: 0;
    padding: 0;
    transition: all ease-in-out 400ms;
    height: 0;
    overflow: hidden;

    &.active {
        max-height: none;
        padding: 0.25rem 0;
        height: 1.9rem;
    }
}

.tabs-component {
    min-height: 500px;
}

.tabs-component-panel {
    overflow: visible !important;
}

.reservations__anchor-see-more-logs {
    cursor: pointer;

    &,
    &:link,
    &:visited,
    &:hover,
    &:active {
        color: #4768b5;
        text-decoration: underline;
    }
}
</style>

<style lang="scss">
@import "~@/assets/scss/_variables.scss";

.reservation-edit__tabs {
    .tabs-component-tabs {
        margin-top: 0.5rem !important;
    }

    .tabs-component-tabs,
    .tabs-component-panels,
    .tabs-component-panel {
        margin-left: 0 !important;
        margin-right: 0 !important;
    }

    .tabs-component-panels section {
        min-height: 480px;
        height: auto;
    }
}

.warning {
    position: relative;

    &::after {
        content: '';
        position: absolute;
        background-color: $color-6;
        border-radius: 50%;
        height: 10px;
        width: 10px;
        display: inline-block;
        top: 10px;
        left: 10px;
    }
}

.editReservationModal {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    overflow: auto;
    padding-top: calc(24px + env(safe-area-inset-top));
    padding-bottom: calc(24px + env(safe-area-inset-bottom));
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
}

.editReservationModal .modal-dialog,
.editReservationModal .modal__dialog,
.editReservationModal .modal-content {
    margin: 0 auto;
    max-height: none;
    overflow: visible;
}
</style>



// WEBPACK FOOTER //
// ReservationEditV2.vue?7d4736ff